import {
	type Message,
	type NavigationTypeType,
	type SDK_Colors,
	type SDK_Fonts,
	type SDK_Stack,
	strings,
	text,
} from "@quickpoint.me/shared";
import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import type { SetterOrUpdater } from "recoil";
import type { AiChatLevel, InlineEditTarget } from "../state/aiChatAtoms";
import {
	fireMoveCursortoPageEvent,
	fireUpdateFilterColorsEvent,
	fireUpdateLoadedFilterFontsEvent,
	goToPage,
} from "./editor/events";
import {
	sendAllFiles,
	sendCodeAndFilterToFileUploader,
	sendCodeToViewer,
	sendFilterNamesToViewer,
} from "./messageBus";

type MessageHandlerDeps = {
	fetchAllFiles: () => Promise<void>;
	deleteStackFromDb: (stackCfKey: string) => Promise<void>;
	deleteImageFromQP: (fileId: string) => Promise<void>;
	saveStackToDb: (stack: SDK_Stack) => Promise<void>;
	setParsedStack: SetterOrUpdater<SDK_Stack>;
	toggleShowStacksDuringDemoMode: () => void;
	hostId: string;
	allFiles: QpFile[];
	filterNames: string[];
	stack: SDK_Stack;
	setFiltersAreLoaded: SetterOrUpdater<number>;
	setPreviewerFontCSS: SetterOrUpdater<string>;
	setStoryNamesAtom: SetterOrUpdater<string[]>;
	setNavigationTypeAtom: SetterOrUpdater<NavigationTypeType>;
	togglePanelFor: (panel: "files" | "share" | "stacks") => void;
	setError: (error: string) => void;
	setStackCfKey: SetterOrUpdater<string>;
	setCurrentFilterColors: SetterOrUpdater<SDK_Colors>;
	currentFilterColors: SDK_Colors;
	viewerPageIndex: number;
	setViewerPageIndex: SetterOrUpdater<number>;
	setInlineEditTarget: SetterOrUpdater<InlineEditTarget | null>;
	setChatLevel: SetterOrUpdater<AiChatLevel>;
};

function extractElementJadle(fullCode: string, lineNumber: number): { jadle: string; startLine: number; endLine: number } {
	const lines = fullCode.split("\n");
	let endLine = lineNumber + 1;
	// Collect indented modifier lines that follow the element
	while (endLine < lines.length && /^[\s\t]+\w/.test(lines[endLine])) {
		endLine++;
	}
	return {
		jadle: lines.slice(lineNumber, endLine).join("\n"),
		startLine: lineNumber,
		endLine,
	};
}

function handleError(message: Message, deps: MessageHandlerDeps) {
	if (!message.error) return;

	let messageError: string;

	if (typeof message.error === "string") {
		messageError = message.error;
	} else if (
		typeof message.error === "object" &&
		message.error !== null &&
		"response" in message.error
	) {
		const errorWithMessage = message.error as {
			response: { errors: { message: string }[] };
		};
		messageError = errorWithMessage.response.errors[0].message;
	} else {
		messageError = "An unknown error occurred";
	}

	if (
		message.action === strings.messages.applicationError &&
		typeof messageError === "string"
	) {
		deps.setError(messageError);
		return;
	}

	if (
		message.action === strings.messages.error &&
		typeof messageError !== "string"
	) {
		console.info("Network error occurred");
		deps.setStackCfKey(strings.utility.networkError);
		return;
	}

	if (message.action === strings.messages.error) {
		if (messageError === strings.common.unauthorized) {
			localStorage.removeItem(strings.localStorage.authObject);
			deps.setError(strings.common.unauthorized);
			return;
		}
		if (messageError === text.errors.emailLinkExpired) {
			deps.setError(strings.utility.emailLinkExpired);
			return;
		}
		if (messageError === text.errors.invalidCredentials) {
			deps.setError(strings.utility.invalidCredentials);
			return;
		}
		if (messageError === strings.utility.unverifiedEmail) {
			deps.setError(strings.utility.unverifiedEmail);
			return;
		}
	}
}

export function handlePostMessage(
	e: MessageEvent<Message>,
	deps: MessageHandlerDeps,
) {
	const message: Message = e.data;
	if (!message.action) return;

	if (message.error) {
		handleError(message, deps);
		return;
	}

	switch (message.action) {
		case strings.messages.updateFiles:
			deps.fetchAllFiles();
			break;
		case strings.messages.deleteStack:
			if (message.payload && typeof message.payload === "string") {
				deps.deleteStackFromDb(message.payload);
			}
			break;
		case strings.messages.deleteImage:
			if (message.payload && typeof message.payload === "string") {
				deps.deleteImageFromQP(message.payload);
			}
			break;
		case strings.messages.parsedStack:
			if (message.payload) {
				deps.setParsedStack(message.payload as SDK_Stack);
			}
			break;
		case strings.messages.requestAllFiles:
			// an empty atom here usually means the initial fetch hasn't landed —
			// replying [] would clobber fresher broadcasts, so fetch instead
			// (fetchAllFiles broadcasts the result itself)
			if (deps.allFiles.length) {
				sendAllFiles(deps.hostId, deps.allFiles);
			} else {
				deps.fetchAllFiles();
			}
			break;
		case strings.messages.requestFilterNames:
			sendFilterNamesToViewer(deps.hostId, deps.filterNames);
			/* a freshly opened pop-out viewer sends this on mount, but never
			   requestStack (Display mounts with container={null}, so its
			   requestStack effect is dead code) — send the current stack too
			   so the pop-out isn't empty until the next edit, then move it to
			   the page currently selected in the author */
			sendCodeToViewer(deps.hostId, deps.stack);
			goToPage(deps.hostId, "QP", deps.viewerPageIndex);
			break;
		case strings.messages.requestStack:
			sendCodeToViewer(deps.hostId, deps.stack);
			break;
		case strings.messages.saveStack:
			if (message.payload) {
				deps.saveStackToDb(message.payload as SDK_Stack);
			}
			break;
		case strings.messages.setFilterColors:
			if (message.payload) {
				deps.setCurrentFilterColors(message.payload as SDK_Colors);
				fireUpdateFilterColorsEvent(message.payload as SDK_Colors);
			}
			break;
		case strings.messages.setFilterFonts:
			if (message.payload) {
				fireUpdateLoadedFilterFontsEvent(message.payload as SDK_Fonts);
			}
			break;

		case strings.messages.toggleStoriesDuringDemoMode:
			deps.toggleShowStacksDuringDemoMode();
			break;
		case strings.messages.filtersAreLoaded:
			deps.setFiltersAreLoaded((i) => i + 1);
			if (message.payload && typeof message.payload === "string") {
				deps.setPreviewerFontCSS(message.payload);
			}
			break;
		case strings.messages.setStoryNames:
			if (message.payload) {
				deps.setStoryNamesAtom(message.payload as string[]);
			}
			break;
		case strings.messages.setNavigationType:
			if (message.payload) {
				deps.setNavigationTypeAtom(message.payload as NavigationTypeType);
			}
			break;
		case strings.messages.toggleFilesPanel:
			deps.togglePanelFor("files");
			break;
		case strings.messages.toggleSharePanel:
			deps.togglePanelFor("share");
			break;
		case strings.messages.toggleStacksPanel:
			deps.togglePanelFor("stacks");
			break;
		case strings.messages.elementClick:
			if (message.payload && typeof message.payload === "object" && "lineNumber" in message.payload) {
				const { lineNumber, pageIndex, elementType } = message.payload as {
					lineNumber: number;
					pageIndex: number;
					elementType: string;
					content: string;
				};
				const code = deps.stack.code || "";
				const { jadle, startLine, endLine } = extractElementJadle(code, lineNumber);
				deps.setInlineEditTarget({
					lineNumber: startLine,
					endLineNumber: endLine,
					pageIndex,
					elementType,
					jadleSnippet: jadle,
				});
				// Only set level if not already at 2 to avoid unnecessary re-renders
				// (Editor uses nanoid() in CM key, so any re-render destroys/recreates CM)
				deps.setChatLevel((prev) => prev === 2 ? prev : 2);
			}
			break;
		case strings.messages.goToPage:
			// pop-out page turn: keep the shared current page — thumbnails,
			// embedded preview panes and the editor cursor all follow (same
			// recipe as a thumbnail click). The pop-out receives the "QP"
			// rebroadcast too, but its LastRemotePage guard makes it a no-op.
			if (typeof message.payload === "number") {
				deps.setViewerPageIndex(message.payload);
				goToPage(deps.hostId, "QP", message.payload);
				goToPage(deps.hostId, "preview", message.payload);
				fireMoveCursortoPageEvent(message.payload);
			}
			break;
		case strings.messages.getCodeAndFilter:
			if (deps.stack.code) {
				// Split by '===' that appears at the beginning of a line (m flag for multiline)
				const pages = deps.stack.code
					.split(/^\s*===\s*$/m)
					.filter((page) => page.trim() !== "");
				const currentPageCode = pages[deps.viewerPageIndex - 1] || "";
				sendCodeAndFilterToFileUploader(
					deps.hostId,
					currentPageCode,
					deps.currentFilterColors,
					deps.stack.stackId,
				);
			}
			break;
		default:
			// Optional: handle unknown actions
			// console.warn("Unknown message action:", message.action);
			break;
	}
}
